import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff, CheckCircle2, XCircle, KeyRound, AlertTriangle, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { changePassword as apiChangePassword } from "@/lib/api";
import Logo from "@/components/Logo";

export default function ChangePassword() {
  const { user, setUser, refresh, signOut } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Policy rules check
  const hasMinLength = newPassword.length >= 8;
  const hasUpper = /[A-Z]/.test(newPassword);
  const hasLower = /[a-z]/.test(newPassword);
  const hasDigit = /[0-9]/.test(newPassword);
  const hasSpecial = /[^A-Za-z0-9]/.test(newPassword);
  const isMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const isDifferentFromCurrent = newPassword.length === 0 || newPassword !== currentPassword;

  const isFormValid =
    currentPassword.length > 0 &&
    hasMinLength &&
    hasUpper &&
    hasLower &&
    hasDigit &&
    hasSpecial &&
    isMatch &&
    isDifferentFromCurrent;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    setError(null);
    setLoading(true);

    try {
      const res = await apiChangePassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      setSuccess(true);
      if (res?.user || res?.id) {
        setUser(res.user || res);
      }
      await refresh();
      setTimeout(() => {
        navigate("/app", { replace: true });
      }, 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg || d).join(", ")
          : "Failed to change password. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12 relative grain-overlay">
        <div>
          <Logo height={56} />
        </div>
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 text-xs font-semibold tracking-wide uppercase border border-amber-500/30">
            <KeyRound size={14} /> Security Action Required
          </div>
          <h2 className="serif text-4xl leading-tight max-w-md">
            Update your temporary <em className="italic" style={{ color: "hsl(45 85% 55%)" }}>password</em>.
          </h2>
          <p className="text-primary-foreground/70 max-w-md text-sm leading-relaxed">
            For account security, new users logging in with a temporary password must establish a new personal password before accessing organization data.
          </p>

          <div className="bg-primary-foreground/5 backdrop-blur border border-primary-foreground/10 rounded-xl p-5 space-y-3 max-w-md text-xs">
            <div className="flex items-center gap-2 text-amber-300 font-medium">
              <ShieldCheck size={16} /> Password Requirements Checklist
            </div>
            <ul className="space-y-2 text-primary-foreground/80">
              <li className="flex items-center gap-2">
                <span className={hasMinLength ? "text-emerald-400" : "text-primary-foreground/40"}>✓</span> At least 8 characters in length
              </li>
              <li className="flex items-center gap-2">
                <span className={hasUpper && hasLower ? "text-emerald-400" : "text-primary-foreground/40"}>✓</span> Uppercase & lowercase letters
              </li>
              <li className="flex items-center gap-2">
                <span className={hasDigit ? "text-emerald-400" : "text-primary-foreground/40"}>✓</span> At least one number (0-9)
              </li>
              <li className="flex items-center gap-2">
                <span className={hasSpecial ? "text-emerald-400" : "text-primary-foreground/40"}>✓</span> At least one special symbol (!@#$%^&*)
              </li>
            </ul>
          </div>
        </div>

        <div className="text-xs text-primary-foreground/50">
          © {new Date().getFullYear()} TrustDime · Security Subsystem
        </div>
      </div>

      {/* Right panel - Form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md space-y-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-sm font-semibold">
                <AlertTriangle size={18} /> Password Change Required
              </div>
              <button
                type="button"
                onClick={signOut}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
              >
                Sign Out
              </button>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Set New Password</h1>
            <p className="text-xs text-muted-foreground">
              Logged in as <strong className="text-foreground">{user?.email}</strong>. Please enter your temporary password and choose a new secure password.
            </p>
          </div>

          {error && (
            <div className="p-3.5 rounded-lg bg-destructive/15 border border-destructive/30 text-destructive text-xs leading-relaxed flex items-start gap-2.5">
              <XCircle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="p-3.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs leading-relaxed flex items-center gap-2">
              <CheckCircle2 size={16} className="shrink-0" />
              <span>Password updated successfully! Redirecting to workspace...</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Current / Temporary Password */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground flex items-center justify-between">
                <span>Current / Temporary Password</span>
              </label>
              <div className="relative">
                <input
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  placeholder="Enter temporary password"
                  className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">New Password</label>
              <div className="relative">
                <input
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Enter new strong password"
                  className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Confirm New Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Re-enter new password"
                  className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Dynamic policy indicator */}
            <div className="p-3.5 rounded-lg border border-border bg-card space-y-2 text-xs">
              <div className="font-semibold text-foreground text-xs">Security Policy Requirements</div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className={`flex items-center gap-1.5 ${hasMinLength ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                  {hasMinLength ? <CheckCircle2 size={13} /> : <XCircle size={13} />} 8+ Characters
                </div>
                <div className={`flex items-center gap-1.5 ${hasUpper ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                  {hasUpper ? <CheckCircle2 size={13} /> : <XCircle size={13} />} Uppercase (A-Z)
                </div>
                <div className={`flex items-center gap-1.5 ${hasLower ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                  {hasLower ? <CheckCircle2 size={13} /> : <XCircle size={13} />} Lowercase (a-z)
                </div>
                <div className={`flex items-center gap-1.5 ${hasDigit ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                  {hasDigit ? <CheckCircle2 size={13} /> : <XCircle size={13} />} Number (0-9)
                </div>
                <div className={`flex items-center gap-1.5 ${hasSpecial ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                  {hasSpecial ? <CheckCircle2 size={13} /> : <XCircle size={13} />} Special Symbol
                </div>
                <div className={`flex items-center gap-1.5 ${isMatch ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                  {isMatch ? <CheckCircle2 size={13} /> : <XCircle size={13} />} Passwords Match
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={!isFormValid || loading || success}
              className="w-full py-2.5 px-4 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="inline-block animate-spin">⏳</span>
              ) : (
                <Lock size={16} />
              )}
              {loading ? "Updating Password..." : "Update Password & Continue"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
